/**
 * Created by Ning Jiang on 2021-06-22.
 */

import {WinPresentationController} from "../../WinPresentationController";
import {gsap, TimelineLite} from "gsap";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {BigWinView} from "./BigWinView";
import {Oktoberfest} from "../../../Oktoberfest";
import {OktoberfestGameConfig} from "../../../OktoberfestGameConfig";
import {OktoberfestParsedGameData} from "../../../server/data/OktoberfestParsedGameData";
import {InitialWinController} from "../initial/InitialWinController";
import {OktoberfestSound} from "../../../sound/OktoberfestSound";
import {GameHelper} from "../../../../core/utils/GameHelper";


export class BigWinController extends WinPresentationController {

    private static SOUND_NAMES: string[] = [
        OktoberfestSound.SOUND_NAME_BIG_WIN_SMALL,
        OktoberfestSound.SOUND_NAME_BIG_WIN_MEDIUM,
        OktoberfestSound.SOUND_NAME_BIG_WIN_LARGE
    ];
    private _view: BigWinView;
    private _levelTimeline: TimelineLite | null;
    private _currentBigWinLevel: number = 0;

    constructor() {
        super("BigWinController", true);
        this._view = new BigWinView();
    }

    protected hasWin(): boolean {
        return Oktoberfest.model.parsedGameData.currentWinRatioConfig!.bigWinLevel > -1;
    }

    protected async createWinAnimation(): Promise<any> {
        await new Promise<any>(resolve => {
            OktoberfestSound.fadeMusic(0, 200);
            this.playBigWinLevel(0, resolve);
        });
    }

    protected skip(): void {
        if (this._levelTimeline && this._levelTimeline.isActive()) {
            this._levelTimeline.progress(1);
            OktoberfestSound.soundPlayer.stopTrack(BigWinController.SOUND_NAMES[this._currentBigWinLevel]);
        }
    }

    private playBigWinLevel(level: number, resolve:(value:any) => void): void {
        if (level > Oktoberfest.model.parsedGameData.currentWinRatioConfig!.bigWinLevel) {
            this._levelTimeline = null;
            OktoberfestSound.soundPlayer.stopTrack(BigWinController.SOUND_NAMES[Oktoberfest.model.parsedGameData.currentWinRatioConfig!.bigWinLevel]);
            OktoberfestSound.playSoundEffect(OktoberfestSound.SOUND_NAME_BIG_WIN_ENDS);
            this.playEnding(resolve);
            const reels: string[][] = Oktoberfest.model.parsedGameData.reels;
            if (( GameHelper.isReel2Stacked(reels) && ( GameHelper.isReel4Stacked(reels) ) ) && !GameHelper.isNextModeNormal()) {
                GameHelper.resetAllSymbols();
            }
        } else {
            this._flowStateController.setSkipCallback(() => this.skip!());
            const tl = gsap.timeline();
            tl.add(this.getLevelAnimation(level));
            tl.add(() => this.playBigWinLevel(level + 1, resolve));
            this._levelTimeline = tl;
        }
    }

    private getLevelAnimation(level: number): TimelineLite {
        const data: OktoberfestParsedGameData = Oktoberfest.model.parsedGameData;
        const currentRatio = OktoberfestGameConfig.WIN_RATIOS.find(r => r.bigWinLevel === level)!;
        const from: number = level === 0 ? 0 : data.playedBetValue * currentRatio.ratio;
        const to: number = level === data.currentWinRatioConfig!.bigWinLevel ? data.singleWin : data.playedBetValue * OktoberfestGameConfig.WIN_RATIOS.find(r => r.bigWinLevel === ( level + 1 ))!.ratio;
        const duration = currentRatio.duration;
        const isLastLevel: boolean = level === data.currentWinRatioConfig!.bigWinLevel;
        const tl: TimelineLite = gsap.timeline();
        tl.add([
            () => {
                Oktoberfest.bigWinCountUp.scale.set(1.2);
            },
            Oktoberfest.bigWinCountUp.getCountUpAnimation(from, to, duration, isLastLevel),
            gsap.to(Oktoberfest.bigWinCountUp.scale, {duration: duration, x: 1, y: 1, ease: `power4.out`}),
            this._view.getLevelAnimation(level)
        ], 0);
        tl.add(() => {
            this._currentBigWinLevel = level;
            OktoberfestSound.playSoundEffect(BigWinController.SOUND_NAMES[level]);
        }, 0);
        return tl;
    }

    private playEnding(resolve: (value:any) => void): void {
        const tl: TimelineLite = new TimelineLite({
            onComplete: () => {
                resolve(this);
                OktoberfestSound.fadeMusic(1, 200);
                const winRatio: number = Oktoberfest.model.parsedGameData.currentWinRatioConfig?.ratio!;
                const position: PIXI.Point = Oktoberfest.reels[2].getSymbol(1).getGlobalPosition();
                Oktoberfest.winEffectView.playAnimation(position, InitialWinController.getEffectLevel(winRatio), false);
                Oktoberfest.initialCountUp.getCountUpAnimation(0, Oktoberfest.model.parsedGameData.singleWin, 1, Oktoberfest.model.parsedGameData.currentMultiplier);
                SlotGame.keypadPlugin.setWin(Oktoberfest.model.parsedGameData.totalWin, Oktoberfest.model.parsedGameData.showTotalWin);
            }
        });
        this._flowStateController.setSkipCallback();

        tl.add(this._view.playEnding(), 0.01);
    }
}
